export type RuleSeverity = 'info' | 'warn' | 'block'

export type RuleMatch =
  | { type: 'regex'; pattern: string; flags?: string }
  | { type: 'keyword_list'; keywords: string[]; case_sensitive?: boolean }
  | { type: 'llm_judge'; prompt: string; expected_output: 'yes_no' | 'classification'; model?: string }
  | { type: 'readability'; metric: 'flesch_kincaid' | 'gunning_fog' | 'smog'; threshold: number; operator: '<' | '<=' | '>' | '>=' }
  | { type: 'composite'; all_of?: RuleMatch[]; any_of?: RuleMatch[] }

export interface IndustryRule {
  rule_id: string
  severity: RuleSeverity
  match: RuleMatch
  message: string
  cited_authority?: string
  suggested_rewrite_template?: string
  jurisdictions?: string[]
}

export interface IndustryPack {
  pack_id: string
  industry: string
  pack_name: string
  version: number
  rules: IndustryRule[]
}

export interface PackFinding {
  rule_id: string
  severity: RuleSeverity
  message: string
  evidence_snippet?: string
  suggested_rewrite?: string
  cited_authority?: string
  matched_text?: string
}

// Readability helpers

export function fleschKincaid(text: string): number {
  const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0).length || 1
  const words = text.split(/\s+/).filter(Boolean)
  const wordCount = words.length || 1
  const syllables = words.reduce((n, w) => n + countSyllables(w), 0)
  return 0.39 * (wordCount / sentences) + 11.8 * (syllables / wordCount) - 15.59
}

function countSyllables(word: string): number {
  const w = word.toLowerCase().replace(/[^a-z]/g, '')
  if (w.length <= 3) return 1
  const matches = w.replace(/(?:[^laeiouy]es|ed|[^laeiouy]e)$/, '').replace(/^y/, '').match(/[aeiouy]{1,2}/g)
  return Math.max(1, matches?.length ?? 1)
}

export function applyRules(text: string, rules: IndustryRule[]): PackFinding[] {
  const findings: PackFinding[] = []

  for (const rule of rules) {
    const finding = evalRule(text, rule)
    if (finding) findings.push(finding)
  }

  return findings
}

function evalRule(text: string, rule: IndustryRule): PackFinding | null {
  const match = rule.match

  if (match.type === 'keyword_list') {
    const haystack = match.case_sensitive ? text : text.toLowerCase()
    const hit = match.keywords.find(kw => {
      const needle = match.case_sensitive ? kw : kw.toLowerCase()
      return haystack.includes(needle)
    })
    if (hit) {
      return {
        rule_id: rule.rule_id,
        severity: rule.severity,
        message: rule.message,
        cited_authority: rule.cited_authority,
        suggested_rewrite: rule.suggested_rewrite_template,
        matched_text: hit,
      }
    }
  }

  if (match.type === 'regex') {
    const re = new RegExp(match.pattern, match.flags ?? 'i')
    const m = re.exec(text)
    if (m) {
      return {
        rule_id: rule.rule_id,
        severity: rule.severity,
        message: rule.message,
        cited_authority: rule.cited_authority,
        suggested_rewrite: rule.suggested_rewrite_template,
        evidence_snippet: m[0],
      }
    }
  }

  if (match.type === 'readability') {
    const score = fleschKincaid(text)
    const passes = evalOperator(score, match.operator, match.threshold)
    if (!passes) {
      return {
        rule_id: rule.rule_id,
        severity: rule.severity,
        message: `${rule.message} (FK grade: ${score.toFixed(1)})`,
        cited_authority: rule.cited_authority,
        suggested_rewrite: rule.suggested_rewrite_template,
      }
    }
  }

  // LLM judge rules are evaluated asynchronously — skip in sync mode
  if (match.type === 'llm_judge') return null

  if (match.type === 'composite') {
    if (match.all_of) {
      const syntheticRule = { ...rule, match: match.all_of[0] }
      const allMatch = match.all_of.every(m => evalRule(text, { ...rule, match: m }) !== null)
      if (allMatch) return evalRule(text, syntheticRule)
    }
    if (match.any_of) {
      for (const m of match.any_of) {
        const r = evalRule(text, { ...rule, match: m })
        if (r) return r
      }
    }
  }

  return null
}

function evalOperator(value: number, op: string, threshold: number): boolean {
  if (op === '<') return value < threshold
  if (op === '<=') return value <= threshold
  if (op === '>') return value > threshold
  if (op === '>=') return value >= threshold
  return false
}
